import React from 'react';
import { Container, Box, Typography, Grid, Card } from '@mui/material';
import { Shield, Lock, Security, Verified, CloudDone, AdminPanelSettings, HistoryEdu } from '@mui/icons-material';
import WebsiteLayout from '../../components/Website/WebsiteLayout';
import HeroSection from '../../components/Website/HeroSection';
import SEO from '../../components/SEO/SEO';
import { getPageSEO } from '../../config/seo';
import { useRegion } from '../../context/RegionContext';

const SecurityPage = () => {
  const seo = getPageSEO('security');
  const { contact } = useRegion();

  const securityFeatures = [
    {
      icon: <Security />,
      title: 'Immutable Financial Snapshots',
      desc: 'Cryptographically signed snapshots of financial states ensure data integrity and audit readiness'
    },
    {
      icon: <AdminPanelSettings />,
      title: 'Enterprise Access Controls',
      desc: 'Enforced MFA, SSO integration, and IP whitelisting for absolute environment control'
    },
    {
      icon: <Verified />,
      title: 'IFRS, UK & US GAAP Compliance',
      desc: 'Built-in controls and reporting frameworks that adhere to global accounting standards'
    },
    {
      icon: <CloudDone />,
      title: 'Sovereign Data Isolation',
      desc: 'Physical and logical data segregation for multi-tenant enterprise architectures'
    },
    {
      icon: <HistoryEdu />,
      title: 'Granular Audit Orchestration',
      desc: 'Every system event and user action is recorded in an immutable, searchable audit log'
    },
    {
      icon: <Shield />,
      title: 'Advanced Threat Protection',
      desc: 'AI-powered anomaly detection and real-time security monitoring across all modules'
    }
  ];

  return (
    <WebsiteLayout>
      <SEO {...seo} />
      <HeroSection chip="Security">
        <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
          <Typography variant="h1" sx={{ fontSize: { xs: 40, md: 56 }, fontWeight: 800, mb: 3, color: 'primary.contrastText' }}>
            Security at FluxERP
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.95, color: 'primary.contrastText' }}>
            Your data security is our top priority. We implement industry-leading security measures to protect your business.
          </Typography>
        </Box>
      </HeroSection>

      <Container maxWidth="lg" sx={{ py: 12 }}>
        <Grid container spacing={4}>
          {securityFeatures.map((feature, idx) => (
            <Grid item xs={12} md={4} key={idx}>
              <Card sx={{ p: 4, height: '100%', border: '1px solid', borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.200', bgcolor: theme => theme.palette.mode === 'dark' ? 'background.paper' : 'white', boxShadow: 'none' }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 2,
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.contrastText',
                    mb: 3
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                  {feature.title}
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  {feature.desc}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'background.default' : 'grey.50', py: 12 }}>
        <Container maxWidth="md">
          <Box sx={{ '& h2': { fontSize: 32, fontWeight: 700, mt: 6, mb: 3 }, '& h3': { fontSize: 24, fontWeight: 600, mt: 4, mb: 2 }, '& p': { mb: 2, lineHeight: 1.8, color: 'text.secondary' } }}>

            <Typography variant="h2">Infrastructure Security</Typography>

            <Typography variant="h3">Cloud Infrastructure</Typography>
            <Typography variant="body1">
              FluxERP is hosted on enterprise-grade cloud infrastructure with:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li>99.9% uptime SLA</li>
              <li>Automatic failover and redundancy</li>
              <li>DDoS protection and firewall</li>
              <li>Regular security patches and updates</li>
              <li>Geographic data replication</li>
            </Box>

            <Typography variant="h3">Database Security</Typography>
            <Typography variant="body1">
              Our PostgreSQL databases are secured with:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li>Encryption at rest using AES-256</li>
              <li>Encrypted connections (SSL/TLS)</li>
              <li>Automated daily backups</li>
              <li>Point-in-time recovery capability</li>
              <li>Database-level access controls</li>
            </Box>

            <Typography variant="h2">Application Security</Typography>

            <Typography variant="h3">Authentication & Authorization</Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li>JWT-based stateless authentication</li>
              <li>Secure password hashing with bcrypt</li>
              <li>Session management with automatic timeout</li>
              <li>Role-based access control (RBAC)</li>
              <li>Multi-factor authentication (MFA) support</li>
              <li>OAuth 2.0 integration ready</li>
            </Box>

            <Typography variant="h3">Data Protection</Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li>Complete data isolation between companies</li>
              <li>Tenant-level data segregation</li>
              <li>Encrypted data transmission (TLS 1.3)</li>
              <li>Secure file upload and storage</li>
              <li>Data sanitization and validation</li>
            </Box>

            <Typography variant="h2">Compliance & Certifications</Typography>
            <Typography variant="body1">
              FluxERP adheres to industry standards and best practices:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li>GDPR compliance for data protection</li>
              <li>SOC 2 Type II certification (in progress)</li>
              <li>ISO 27001 security standards</li>
              <li>PCI DSS compliance for payment processing</li>
              <li>Regular third-party security audits</li>
            </Box>

            <Typography variant="h2">Monitoring & Response</Typography>

            <Typography variant="h3">24/7 Monitoring</Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li>Real-time security monitoring</li>
              <li>Intrusion detection systems</li>
              <li>Automated threat detection</li>
              <li>Log aggregation and analysis</li>
              <li>Performance and availability monitoring</li>
            </Box>

            <Typography variant="h3">Incident Response</Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li>Dedicated security team</li>
              <li>24/7 incident response capability</li>
              <li>Documented incident response procedures</li>
              <li>Regular security drills and testing</li>
              <li>Transparent communication during incidents</li>
            </Box>

            <Typography variant="h2">Audit & Logging</Typography>
            <Typography variant="body1">
              Comprehensive audit trails track:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li>User login and logout activities</li>
              <li>Data access and modifications</li>
              <li>Permission changes</li>
              <li>Financial transactions</li>
              <li>System configuration changes</li>
              <li>Failed authentication attempts</li>
            </Box>

            <Typography variant="h2">Data Backup & Recovery</Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li>Automated daily backups</li>
              <li>Multiple backup locations</li>
              <li>30-day backup retention</li>
              <li>Encrypted backup storage</li>
              <li>Tested disaster recovery procedures</li>
              <li>Point-in-time recovery capability</li>
            </Box>

            <Typography variant="h2">Employee Security</Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li>Background checks for all employees</li>
              <li>Regular security training</li>
              <li>Strict access control policies</li>
              <li>Non-disclosure agreements (NDAs)</li>
              <li>Principle of least privilege</li>
            </Box>

            <Typography variant="h2">Vulnerability Management</Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li>Regular penetration testing</li>
              <li>Automated vulnerability scanning</li>
              <li>Bug bounty program</li>
              <li>Responsible disclosure policy</li>
              <li>Rapid patch deployment</li>
            </Box>

            <Typography variant="h2">Your Responsibilities</Typography>
            <Typography variant="body1">
              While we provide robust security, you should also:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li>Use strong, unique passwords</li>
              <li>Enable multi-factor authentication</li>
              <li>Keep your credentials confidential</li>
              <li>Report suspicious activities immediately</li>
              <li>Regularly review user access permissions</li>
              <li>Educate your team on security best practices</li>
            </Box>

            <Typography variant="h2">Security Contact</Typography>
            <Typography variant="body1">
              If you discover a security vulnerability or have security concerns, please contact us immediately:
            </Typography>
            <Box sx={{ mt: 2, p: 3, bgcolor: theme => theme.palette.mode === 'dark' ? 'background.paper' : 'white', borderRadius: 2, border: '1px solid', borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.200' }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>Email: {contact.email}</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>Phone: {contact.phone}</Typography>
              <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                We take all security reports seriously and will respond within 24 hours.
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </WebsiteLayout>
  );
};

export default SecurityPage;
